import React, { ReactElement, useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import MuiSelect, { SelectProps as MuiSelectProps, SelectChangeEvent } from '@mui/material/Select'
import { MenuItem, Typography } from '@mui/material'

export interface SelectOption {
  label?: String
  value: string | number
}

export type FormSelectProps = MuiSelectProps & {
  name: string
  optKey?: string
  control: any
  options: SelectOption[]
}

export default function FormSelect({ name, control, label, options, ...other }: FormSelectProps): ReactElement {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <>
          <Typography variant="inputLabelColored" id={`select-${name}-label`}>
            {label}
          </Typography>
          <MuiSelect
            sx={{ mb: 4 }}
            fullWidth
            label={label}
            labelId={`select-${name}-label`}
            variant="standard"
            onChange={onChange}
            value={value}
            {...other}
          >
            {options &&
              options.map((option: SelectOption, idx: number) => {
                return (
                  <MenuItem key={idx} value={option.value}>
                    {option.label}
                  </MenuItem>
                )
              })}
          </MuiSelect>
        </>
      )}
    />
  )
}

function extractItemValue(props: Omit<FormSelectProps, 'control'>, item: Record<string, string>) {
  return item ? (typeof item === 'object' ? item[props.optKey || 'value'] : item) : ''
}

function extractOptionValue(
  props: Omit<FormSelectProps, 'control'>,
  option: SelectOption | { value: string | Record<string, string> } | any,
) {
  const value = typeof option.value === 'object' ? option.value?.[props.optKey || 'value'] : option.value
  return value
}
export function SelectWithForm(props: Omit<FormSelectProps, 'control'>): ReactElement {
  const { control } = useFormContext()

  return (
    <Controller
      name={props.name}
      control={control}
      render={({ field: { onChange, value } }) => {
        return (
          <>
            <Typography variant="inputLabelColored" id={`select-${props.name}-label`}>
              {props.label}
            </Typography>
            <MuiSelect
              fullWidth
              label={props.label}
              labelId={`select-${props.name}-label`}
              variant="standard"
              onChange={(event: SelectChangeEvent) => {
                const selectedOption = props.options.find((it) => {
                  return event.target.value === extractOptionValue(props, it)
                })
                onChange(selectedOption?.value)
              }}
              value={extractItemValue(props, value)}
              disabled={props.disabled}
            >
              {props.options &&
                props.options.map((option: SelectOption, idx: number) => {
                  return (
                    <MenuItem key={idx} value={extractOptionValue(props, option)}>
                      {option.label}
                    </MenuItem>
                  )
                })}
            </MuiSelect>
          </>
        )
      }}
    />
  )
}
